import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import config from 'config'
import { orderBy } from 'lodash'
import {
  CARS_BOOKED_STATE_NOT_ASSIGNED,
  isCarsBookedAssignedState,
} from 'lib/carsBookedStates'
import { getCarContextKey } from 'lib/carContextKey'
import { insertMessagesInItems } from 'lib/messagesLib'

import useSnackbar from '../Snackbar/useSnackbar'

const CARS_BOOKED_ENDPOINT = 'cars-booked'
const CARS_TO_BUY_BLIND_ENDPOINT = 'cars-to-buy-blind'

const hasPartnerBlindSelection = (car) => {
  const blindPrice = car?.blindPrice ?? car?.reprice
  return (
    (blindPrice !== null && blindPrice !== undefined && blindPrice !== '') ||
    !!car?.firstChoice ||
    car?.choice === 'first'
  )
}

const resolveAssignedState = (existingAssignedState, blindAssignedState) => {
  if (isCarsBookedAssignedState(existingAssignedState)) {
    return existingAssignedState
  }
  if (isCarsBookedAssignedState(blindAssignedState)) {
    return blindAssignedState
  }

  return (
    existingAssignedState ||
    blindAssignedState ||
    CARS_BOOKED_STATE_NOT_ASSIGNED
  )
}

const mergeCarsBookedWithBlindCars = (
  carsBooked = [],
  blindCars = [],
  fallbackSessionId = null
) => {
  const mergedByContext = new Map(
    carsBooked
      .map((car) => [getCarContextKey(car, fallbackSessionId), { ...car }])
      .filter(([key]) => key)
  )

  const selectedBlindCars = blindCars.filter((car) =>
    hasPartnerBlindSelection(car)
  )

  selectedBlindCars.forEach((blindCar) => {
    const blindPrice = blindCar?.blindPrice ?? blindCar?.reprice
    const blindFields = {
      blindPrice,
      blindOfferCount: blindCar?.blindOfferCount ?? blindCar?.blindCount,
      blindCount: blindCar?.blindCount ?? blindCar?.blindOfferCount,
      assignedPartnerId: blindCar?.assignedPartnerId,
      autoAssignmentPartnerId: blindCar?.autoAssignmentPartnerId,
      isBlindSelection: true,
    }
    const blindContextKey = getCarContextKey(blindCar, fallbackSessionId)

    if (!blindContextKey) {
      return
    }

    const existing = mergedByContext.get(blindContextKey)

    if (existing) {
      mergedByContext.set(blindContextKey, {
        ...existing,
        ...blindFields,
        assignedState: resolveAssignedState(
          existing?.assignedState,
          blindCar?.assignedState
        ),
      })
      return
    }

    mergedByContext.set(blindContextKey, {
      ...blindCar,
      ...blindFields,
      firstChoice: false,
      secondChoice: false,
      assignedState: blindCar?.assignedState || CARS_BOOKED_STATE_NOT_ASSIGNED,
    })
  })

  return Array.from(mergedByContext.values())
}

const enrichCarsBookedForPartner = (cars = [], partnerId) =>
  cars.map((car) => ({
    ...car,
    isAssignedToPartner:
      !!partnerId &&
      (car?.assignedPartnerId === partnerId ||
        car?.autoAssignmentPartnerId === partnerId),
  }))

const useCarsBooked = () => {
  const { t } = useTranslation()
  const { showSnackbarMessage } = useSnackbar()

  const [state, setState] = useState({
    isFetching: false,
    isUpdating: false,
    cars: [],
    total: 0,
    error: null,
  })

  const setStartFetching = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: true,
      isUpdating: false,
    }))
  }, [setState])

  const setStopFetching = useCallback(
    (error) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        error: error,
      }))
    },
    [setState]
  )

  // const setStartUpdating = useCallback(() => {
  //   setState((s) => ({
  //     ...s,
  //     isFetching: false,
  //     isUpdating: true,
  //   }))
  // }, [setState])

  // const setStopUpdating = useCallback(() => {
  //   setState((s) => ({
  //     ...s,
  //     isUpdating: false,
  //   }))
  // }, [setState])

  const setSuccessFetching = useCallback(
    (data) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        isUpdating: false,
        ...data,
        error: null,
      }))
    },
    [setState]
  )

  const fetchCarsBooked = useCallback(
    async (sessionId, partnerId) => {
      setStartFetching()
      try {
        const results = await axios.get(
          `${config.apiBaseUrl}sessions/${sessionId}/partners/${partnerId}/${CARS_BOOKED_ENDPOINT}`
        )

        const carsBooked = orderBy(results.data.body.items, ['title'], ['asc'])

        let blindCars = []
        try {
          const blindResults = await axios.get(
            `${config.apiBaseUrl}sessions/${sessionId}/partners/${partnerId}/${CARS_TO_BUY_BLIND_ENDPOINT}`
          )
          blindCars = blindResults?.data?.body?.items || []
        } catch (e) {
          blindCars = []
        }

        const messagesResult = await axios.get(
          `${config.apiBaseUrl}sessions/${sessionId}/messages`
        )

        const messages = messagesResult.data.body.messages.filter(
          (message) => message.partnerId === partnerId
        )

        const cars = orderBy(
          enrichCarsBookedForPartner(
            mergeCarsBookedWithBlindCars(carsBooked, blindCars, sessionId),
            partnerId
          ),
          ['title'],
          ['asc']
        )

        insertMessagesInItems(messages, cars, { fallbackSessionId: sessionId })

        setSuccessFetching({
          cars,
          total: cars.length,
        })
      } catch (e) {
        const message =
          e.response?.data?.message ||
          t('common.error.fetchData', {
            attribute: e.response?.status,
          })

        setStopFetching(e)

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [
      setStartFetching,
      setStopFetching,
      setSuccessFetching,
      showSnackbarMessage,
      t,
    ]
  )

  return {
    cars: state.cars,
    total: state.total,
    isFetching: state.isFetching,
    isUpdating: state.isUpdating,
    error: state.error,
    fetchCarsBooked,
  }
}

export default useCarsBooked
